import { apiClient } from '@/api/client'
import type {
  AddPlanSpotParams,
  CreateRoutePlanParams,
  DeepOptimizeJob,
  MapsSegment,
  PlanEndpointRole,
  PlanOrigin,
  PlanPlaceUsage,
  PlanShareUser,
  RoutePlanDetail,
  RoutePlanRoute,
  RoutePlanSummary,
  ShareCandidate,
  SplitPlanDaysParams,
  SplitPlanDaysResult,
  SuggestPlanParams,
  SuggestPreview,
  UpdatePlanSharesParams,
  UpdateRoutePlanItemParams,
  UpdateRoutePlanParams,
} from '@/api/types/route-plan'

export const getRoutePlans = async (signal?: AbortSignal): Promise<RoutePlanSummary[]> => {
  const res = await apiClient.get('/route-plans', { signal })
  return res.data
}

/** 一覧が経路を省いたプランの分だけ取りに行く。axios の既定は ids[]= になるので自前で組む */
export const getRoutePlanRoutes = async (ids: number[], signal?: AbortSignal): Promise<RoutePlanRoute[]> => {
  const params = new URLSearchParams()
  for (const id of ids) params.append('ids', String(id))
  const res = await apiClient.get(`/route-plans/routes?${params.toString()}`, { signal })
  return res.data
}

export const getRoutePlan = async (planId: number, signal?: AbortSignal): Promise<RoutePlanDetail> => {
  const res = await apiClient.get(`/route-plans/${planId}`, { signal })
  return res.data
}

/** 出発地の初期値。直近のプラン → 拠点 Place の順に解決される */
export const getDefaultOrigin = async (signal?: AbortSignal): Promise<PlanOrigin | null> => {
  const res = await apiClient.get('/route-plans/default-origin', { signal })
  return res.data.origin
}

/** 場所ごとの、その場所を含むプラン一覧。地図で追加済みのスポットを隠すのに使う */
export const getPlanPlaceUsages = async (signal?: AbortSignal): Promise<PlanPlaceUsage[]> => {
  const res = await apiClient.get('/route-plans/place-usages', { signal })
  return res.data
}

export const createRoutePlan = async (params: CreateRoutePlanParams): Promise<RoutePlanDetail> => {
  const res = await apiClient.post('/route-plans', params)
  return res.data
}

export const updateRoutePlan = async (planId: number, params: UpdateRoutePlanParams): Promise<RoutePlanDetail> => {
  const res = await apiClient.patch(`/route-plans/${planId}`, params)
  return res.data
}

/** プランの日付で旅の記録を作る。同じ日の旅が既にあればそれを返す */
export const createRoutePlanTrip = async (planId: number): Promise<{ trip_id: number; trip_title: string }> => {
  const res = await apiClient.post(`/route-plans/${planId}/trip`)
  return res.data
}

export const deleteRoutePlan = async (planId: number): Promise<void> => {
  await apiClient.delete(`/route-plans/${planId}`)
}

/** 条件とスポットを引き継いだ下書きプランを作る。最適化結果と共有先は引き継がない */
export const duplicateRoutePlan = async (planId: number): Promise<RoutePlanDetail> => {
  const res = await apiClient.post(`/route-plans/${planId}/duplicate`)
  return res.data
}

export const addRoutePlanItems = async (planId: number, placeIds: number[]): Promise<RoutePlanDetail> => {
  const res = await apiClient.post(`/route-plans/${planId}/items`, { place_ids: placeIds })
  return res.data
}

/** 場所検索の結果をそのままプランに入れる。ラリーに属さない行き先用 */
export const addRoutePlanSpot = async (planId: number, params: AddPlanSpotParams): Promise<RoutePlanDetail> => {
  const res = await apiClient.post(`/route-plans/${planId}/spots`, params)
  return res.data
}

export const deleteRoutePlanItem = async (planId: number, placeId: number): Promise<RoutePlanDetail> => {
  const res = await apiClient.delete(`/route-plans/${planId}/items/${placeId}`)
  return res.data
}

/** 滞在時間と到着希望の更新。到着希望を変えると再最適化が必要になる */
export const updateRoutePlanItem = async (
  planId: number,
  placeId: number,
  params: UpdateRoutePlanItemParams,
): Promise<RoutePlanDetail> => {
  const res = await apiClient.patch(`/route-plans/${planId}/items/${placeId}`, params)
  return res.data
}

/** 手動並べ替え。最適化 API は呼ばないので追加の課金は発生しない */
export const reorderRoutePlan = async (planId: number, placeIds: number[]): Promise<RoutePlanDetail> => {
  const res = await apiClient.post(`/route-plans/${planId}/reorder`, { place_ids: placeIds })
  return res.data
}

/** 手動並べ替えを捨てて最後の最適化結果へ戻す。最適化 API は呼ばない */
export const restoreRoutePlanOrder = async (planId: number): Promise<RoutePlanDetail> => {
  const res = await apiClient.post(`/route-plans/${planId}/restore-order`)
  return res.data
}

/** プラン内のスポットを出発地・到着地にする。placeId が null なら解除 */
export const pinRoutePlanEndpoint = async (
  planId: number,
  role: PlanEndpointRole,
  placeId: number | null,
): Promise<RoutePlanDetail> => {
  const res = await apiClient.post(`/route-plans/${planId}/pin`, { role, place_id: placeId })
  return res.data
}

/** スポット数ぶん課金されるため、条件が同じなら force を付けない限り実行されない */
export const optimizeRoutePlan = async (planId: number, force = false): Promise<RoutePlanDetail> => {
  const res = await apiClient.post(`/route-plans/${planId}/optimize`, undefined, { params: { force } })
  return res.data
}

/** 同じ出発地からの日帰りに分けて最適化する。課金は日数に関係なくスポット数ぶん */
export const splitRoutePlanIntoDays = async (
  planId: number,
  params: SplitPlanDaysParams,
): Promise<SplitPlanDaysResult> => {
  const res = await apiClient.post(`/route-plans/${planId}/split-days`, params)
  return res.data
}

/** じっくりモード。受け付けたらすぐ返り、結果は getDeepSplit とプッシュ通知で受け取る */
export const startDeepSplit = async (planId: number, params: SplitPlanDaysParams): Promise<DeepOptimizeJob> => {
  const res = await apiClient.post(`/route-plans/${planId}/split-days/deep`, params)
  return res.data
}

export const getDeepSplit = async (planId: number, signal?: AbortSignal): Promise<DeepOptimizeJob | null> => {
  const res = await apiClient.get(`/route-plans/${planId}/split-days/deep`, { signal })
  return res.data.job
}

export const getMapsSegments = async (planId: number, signal?: AbortSignal): Promise<MapsSegment[]> => {
  const res = await apiClient.get(`/route-plans/${planId}/maps-url`, { signal })
  return res.data.segments
}

/** 候補件数と消費見込みだけ先に取る。最適化は走らない */
export const previewSuggestion = async (params: SuggestPlanParams): Promise<SuggestPreview> => {
  const res = await apiClient.post('/route-plans/suggest/preview', params)
  return res.data
}

export const suggestRoutePlan = async (params: SuggestPlanParams): Promise<RoutePlanDetail> => {
  const res = await apiClient.post('/route-plans/suggest', params)
  return res.data
}

/** 共有先に選べるユーザー一覧 */
export const getRoutePlanShareCandidates = async (signal?: AbortSignal): Promise<ShareCandidate[]> => {
  const res = await apiClient.get('/route-plans/share-candidates', { signal })
  return res.data
}

export const getRoutePlanShares = async (planIds: number[], signal?: AbortSignal): Promise<PlanShareUser[]> => {
  const params = new URLSearchParams()
  for (const id of planIds) params.append('ids', String(id))
  const res = await apiClient.get(`/route-plans/bulk/shares?${params.toString()}`, { signal })
  return res.data
}

/** 共有先の足し引き。プランごとに共有先が違っても、指定しなかった人はそのまま残る */
export const updateRoutePlanShares = async (params: UpdatePlanSharesParams): Promise<PlanShareUser[]> => {
  const res = await apiClient.post('/route-plans/bulk/shares', params)
  return res.data
}

export const deleteRoutePlans = async (planIds: number[]): Promise<number[]> => {
  const res = await apiClient.post('/route-plans/bulk/delete', { plan_ids: planIds })
  return res.data.deleted_ids
}

/** プランの行からスタンプを押す。共有先でも叩ける */
export const createPlanStamp = async (
  planId: number,
  placeId: number,
  rallyItemId: number,
): Promise<RoutePlanDetail> => {
  const res = await apiClient.post(`/route-plans/${planId}/items/${placeId}/stamps/${rallyItemId}`)
  return res.data
}

export const deletePlanStamp = async (
  planId: number,
  placeId: number,
  rallyItemId: number,
): Promise<RoutePlanDetail> => {
  const res = await apiClient.delete(`/route-plans/${planId}/items/${placeId}/stamps/${rallyItemId}`)
  return res.data
}
